import React, { useEffect, useRef, useState } from 'react';
import Script from 'next/script';
import dynamic from 'next/dynamic';
import { round } from '@virginexperiencedays/components-v2/src/utils/roundPrice';
import { useFeatureFlagsServer } from '@virginexperiencedays/feature-flags/src/useFlags';
import {
  getStripePaymentRequest,
  getDetailsOnShippingChange,
  postToStripePaymentRequestApi,
} from './utils';

const StripeElement = dynamic(() => import('./StripeElement'));

type DeliveryOptions = {
  sourceCode: string;
  price: number;
  name: string;
  description: string;
};

export type SortedDeliveryOptions = {
  id: string;
  label: string;
  detail: string;
  amount: number;
};

export type StripeProps = {
  className?: string;
  productSlug?: string;
  deliveryOptions?: DeliveryOptions[];
  displayPrice?: number;
  productId?: number;
  onLoad?: (data: { paymentMethod: string }) => void;
  onClick?: (data: { paymentMethod: string }) => void;
  onSuccess?: (data: { paymentMethod: string }) => void;
  onError?: (data: { paymentMethod: string }) => void;
};

enum ApplePaySource {
  Undefined,
  Product,
  Basket,
  Checkout,
}

const STRIPE_MOUNT_DELAY_IN_SECONDS = 10;

const Stripe = ({
  className,
  productSlug,
  deliveryOptions,
  displayPrice,
  productId,
  onLoad,
  onClick,
  onSuccess,
  onError,
}: StripeProps) => {
  const [isEnabledServer] = useFeatureFlagsServer();
  const isNewPdp = isEnabledServer('FF_new_pdp');

  const mountTimer = useRef<number | null>(null);

  const [stripePaymentMethod, setStripePaymentMethod] = useState('');
  const [mountStripe, setMountStripe] = useState(false);
  const [loadComponent, setLoadComponent] = useState(false);
  const [stripe, setStripe] = useState<any>();
  const [showStripe, setShowStripe] = useState<boolean>(false);
  const stripeDisplayPrice = round(displayPrice * 100, 2); // - Stripe uses the currency’s smallest unit - i.e. pence

  //@TODO: Remove filtering once delivery options comes with no duplicates // Sorted by price (ascending)
  const uniqueDeliveryOptions = deliveryOptions
    .filter(
      (option, index, array) => array.findIndex((o) => o.sourceCode === option.sourceCode) === index
    )
    .sort((a: { price: number }, b: { price: number }) => a.price - b.price);

  const sortedDeliveryOptions = uniqueDeliveryOptions.map(
    ({ sourceCode, name, description, price }): SortedDeliveryOptions => {
      return {
        id: sourceCode,
        label: name,
        detail: description,
        amount: price * 100,
      };
    }
  );

  const handleScriptReady = () => {
    if (!stripe && window?.Stripe) {
      setStripe(window.Stripe(process.env.NEXT_PUBLIC_STRIPE_PK));
    }
  };

  // Mount stripe script on scroll or after STRIPE_MOUNT_DELAY_IN_SECONDS
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Break if already mounted or no delivery options
    if (mountStripe || !sortedDeliveryOptions?.length) return;

    const handleMountStripe = () => {
      if (mountTimer.current) clearTimeout(mountTimer.current);
      setMountStripe(true);
    };

    mountTimer.current = window.setTimeout(() => {
      handleMountStripe();
    }, 1000 * STRIPE_MOUNT_DELAY_IN_SECONDS);

    window.addEventListener('scroll', handleMountStripe);

    return () => {
      window.removeEventListener('scroll', handleMountStripe);
      if (mountTimer.current) clearTimeout(mountTimer.current);
    };
  }, [mountStripe, sortedDeliveryOptions]);

  // Create payment button
  useEffect(() => {
    if (!stripe) {
      setShowStripe(false);
    } else {
      // Lazy load UI part as soon as stripe is loaded
      setLoadComponent(true);
      // postData is built and sent to /checkout/apistripepaymentrequest
      let postData: any = {
        productId,
        qtySelected: 1,
        personalisationFields: null,
        source: ApplePaySource.Product,
        selectedShippingMethod: sortedDeliveryOptions[0].id,
      };
      const initialAmount = round(stripeDisplayPrice + sortedDeliveryOptions[0].amount, 2);
      // Create Payment request instance
      const request = getStripePaymentRequest(
        initialAmount,
        stripeDisplayPrice,
        sortedDeliveryOptions[0].amount,
        sortedDeliveryOptions
      );
      const paymentRequest = stripe.paymentRequest(request);
      let paymentRequestButtonTimeoutId;

      // IIFE
      (async () => {
        const canMakePayment = await isPaymentPossible(paymentRequest);

        if (canMakePayment) {
          const paymentMethod = getPaymentMethod(canMakePayment, paymentRequest);
          setStripePaymentMethod(paymentMethod);

          // Render payment only if possible
          setShowStripe(true);

          const elements = stripe.elements();
          const prButton = createPaymentRequestButton(elements, paymentRequest, isNewPdp);
          // ensure that all the pending renderings have finished:
          paymentRequestButtonTimeoutId = setTimeout(() => {
            prButton.mount('#payment-request-button');
          }, 0);

          paymentRequest.on('shippingoptionchange', (event) => {
            const {
              updateWith,
              shippingOption: { id, amount },
            } = event;

            postData.selectedShippingMethod = id;
            const updatedAmount = round(stripeDisplayPrice + amount, 2);
            const updateDetails = getDetailsOnShippingChange(
              updatedAmount,
              stripeDisplayPrice,
              amount
            );

            updateWith(updateDetails);
          });

          // event fired on click of payment button
          paymentRequest.on('token', async (event) => {
            const { shippingAddress, complete } = event;
            postData.data = event;
            postData.billingAddress = shippingAddress;
            postData.deliveryAddress = shippingAddress;

            const paymentMethod = event?.methodName;
            const callbackData = { paymentMethod };

            onClick?.(callbackData);

            try {
              await postToStripePaymentRequestApi(postData);

              onSuccess?.(callbackData);

              complete('success');
              window.location.href = `/checkout/thankyou`;
            } catch (error) {
              console.error(error);

              onError?.(callbackData);

              complete('fail');
            }
          });
        }
      })();

      return () => {
        clearTimeout(paymentRequestButtonTimeoutId);
      };
    }
  }, [productId, sortedDeliveryOptions, stripe, stripeDisplayPrice]);

  return (
    mountStripe && (
      <div className={className} data-testid="stripe">
        <Script
          src="https://js.stripe.com/v3/"
          strategy="lazyOnload"
          onReady={handleScriptReady}
          data-testid="stripe-script"
        />
        {loadComponent && (
          <StripeElement
            productSlug={productSlug}
            show={showStripe}
            onLoad={onLoad}
            paymentMethod={stripePaymentMethod}
          />
        )}
      </div>
    )
  );
};

export default Stripe;

function createPaymentRequestButton(elements, paymentRequest, isNewPdp = false) {
  return elements.create('paymentRequestButton', {
    paymentRequest,
    style: {
      paymentRequestButton: {
        height: isNewPdp ? '56px' : '48px', // design req
      },
    },
  });
}

async function isPaymentPossible(paymentRequest) {
  try {
    return await paymentRequest.canMakePayment();
  } catch (e) {
    console.error('#isPaymentPossible, Failed to check if payment can be made');
  }
}

function getPaymentMethod(canMakePayment, paymentRequest) {
  if (canMakePayment?.applePay || paymentRequest?._buttonTypeName === 'APPLE_PAY') {
    return 'apple-pay';
  } else if (canMakePayment?.googlePay || paymentRequest?._buttonTypeName === 'GOOGLE_PAY') {
    return 'google-pay';
  }

  return paymentRequest?._buttonTypeName;
}
